import { applyRevision } from "@/data-preparation-tool/editing/apply-revision";
import { revisionToEdit } from "@/data-preparation-tool/inspect-and-publish/refine-and-publish-utils";
import { loadRevisionDataThunk } from "@/data-preparation-tool/loading/load-revision-data-thunk";
import {
  selectRegistrationRevision,
  selectRevisionEntity,
} from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  CircularProgress,
  FaroMenuItem,
  FaroMenuItemProps,
  FaroTooltip,
  LockIcon,
} from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import {
  CaptureTreeEntityRevision,
  ProjectApi,
  RegistrationRevision,
  RegistrationStatus,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { JSX, useCallback, useState } from "react";

type LockClusterContextMenuActionItemProps = {
  entity: CaptureTreeEntityRevision;
  disabled?: boolean;
  onLockToggled(): void;
};

/**
 * @returns A context menu item to lock or unlock a cluster.
 *   Locking sets the cluster's registration status to `lockedManually`,
 *   unlocking sets it back to `unknown`.
 */
export function LockClusterContextMenuActionItem({
  entity,
  disabled,
  onLockToggled,
}: LockClusterContextMenuActionItemProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const { projectApiClient } = useApiClientContext();

  const [isProcessing, setIsProcessing] = useState(false);

  // Determine the lock state from the same source as the cluster icon.
  const isLocked =
    entity.registrationStatus === RegistrationStatus.lockedManually;

  const parentEntity = useAppSelector(
    selectRevisionEntity(entity.parentId ?? undefined),
  );
  const isParentLocked =
    parentEntity?.registrationStatus === RegistrationStatus.lockedManually;
  // Unlocking a cluster whose parent is locked has no effect on the backend, since the parent's
  // lock takes precedence, so disable the action to avoid a misleading no-op.
  const isUnlockDisabled = isLocked && isParentLocked;

  const onToggleLock = useCallback(async () => {
    if (isProcessing || isUnlockDisabled) return;

    const state = getState();
    const currentRevision = selectRegistrationRevision(state);
    if (!currentRevision) return;

    const newStatus = isLocked
      ? RegistrationStatus.unknown
      : RegistrationStatus.lockedManually;

    setIsProcessing(true);
    try {
      await setClusterRegistrationStatus(
        projectApiClient,
        currentRevision,
        state,
        entity.id,
        newStatus,
      );
      await dispatch(loadRevisionDataThunk({ projectApiClient }));
      // On success the menu closes and this component unmounts, so we don't need to reset `isProcessing` here.
    } catch (error) {
      // Re-enable the action so the user can retry on failure.
      setIsProcessing(false);
      throw error;
    }

    onLockToggled();
  }, [
    isProcessing,
    isUnlockDisabled,
    getState,
    isLocked,
    projectApiClient,
    entity.id,
    dispatch,
    onLockToggled,
  ]);

  const menuItem = (
    <FaroMenuItem
      key="lockCluster"
      label={isLocked ? "Unlock Cluster (DEV)" : "Lock Cluster (DEV)"}
      Icon={getLockMenuIcon(isProcessing, isLocked)}
      sx={{ "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
      onClick={onToggleLock}
      disabled={(disabled ?? false) || isProcessing || isUnlockDisabled}
    />
  );

  if (!isUnlockDisabled) {
    return menuItem;
  }

  return (
    <FaroTooltip title="Parent cluster is locked.">
      <span>{menuItem}</span>
    </FaroTooltip>
  );
}

/**
 * @param isProcessing whether the lock/unlock request is currently in flight.
 * @param isLocked whether the cluster is currently locked.
 * @returns the icon component to show in the lock/unlock menu item.
 */
function getLockMenuIcon(
  isProcessing: boolean,
  isLocked: boolean,
): NonNullable<FaroMenuItemProps["Icon"]> {
  if (isProcessing) {
    return SpinnerIcon;
  }
  return isLocked ? LockOpenIcon : LockIcon;
}

/** @returns a spinner sized for the `Icon` slot of a FaroMenuItem */
function SpinnerIcon(): JSX.Element {
  return <CircularProgress size="1.25rem" />;
}

/**
 * Patches the registration status of the given cluster in a dedicated revision
 * and applies it to the draft.
 *
 * @param projectApiClient client to talk to the project API
 * @param currentRevision the currently active registration revision (the draft)
 * @param state the current application state
 * @param clusterId the ID of the cluster to update
 * @param registrationStatus the registration status to set on the cluster
 */
async function setClusterRegistrationStatus(
  projectApiClient: ProjectApi,
  currentRevision: RegistrationRevision,
  state: RootState,
  clusterId: GUID,
  registrationStatus: RegistrationStatus,
): Promise<void> {
  const editRevision = await revisionToEdit(
    currentRevision,
    projectApiClient,
    state,
  );

  await projectApiClient.createOrUpdateClusterEntitiesForRegistrationRevision({
    registrationRevisionId: editRevision.id,
    requestBody: [{ id: clusterId, registrationStatus }],
  });

  await applyRevision({ revision: editRevision, projectApiClient });
}
